import React from 'react';
import ReactMarkdown from 'react-markdown';
import styles from './position-description.module.css';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { PositionDescription } from '../onboarding/onboarding.service';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import rehypeRaw from 'rehype-raw';
import { trackApplyButtonClick } from '../analytics/analytics.service';
import { UserRole } from '../user/user.type';

type PositionDescriptionUiProps = {
  roleData?: PositionDescription | null;
  applyButton?: boolean;
  isAuthenticated?: boolean;
  jobRoleId?: string;
}

const ApplyButtonComponent = ({ isAuthenticated, jobRoleId }: { isAuthenticated?: boolean; jobRoleId?: string }) => {

  const handleApplyClick = () => {
    trackApplyButtonClick();
  };

  return (
    <Link
      to={isAuthenticated ? '/dashboard' : '/signup'}
      state={{ userRole: UserRole.SoftwareEngineer }}
      className={classNames(
        styles.button,
        styles.applyButton,
      )}
      onClick={handleApplyClick}
    >
      Apply Now
    </Link>
  );
};

const PositionDescriptionUi = (props: PositionDescriptionUiProps) => {
  const {
    roleData,
    applyButton,
    isAuthenticated,
    jobRoleId,
  } = props;

  const jobTitle = roleData?.jobTitle || 'Software Engineer / Tech Co-founder';
  const location = roleData?.location || 'London,';
  const workStyle = roleData?.workStyle || 'Hybrid';
  const commitment = roleData?.commitment || 'Full-Time';
  const description = roleData?.description || [];

  return (
    <div className={styles.content}>
      <div className={styles.title}>
        {jobTitle}
      </div>
      <div className={styles.subtitle}>
        <LocationOnIcon fontSize="small" />
        <div className={styles.subtext}>
          {location} | {workStyle} | {commitment}
        </div>
      </div>
      {applyButton && (
        <div className={styles.buttonWrapper}>
          <ApplyButtonComponent isAuthenticated={isAuthenticated} jobRoleId={jobRoleId} />
        </div>
      )}

      <div className={styles.contentTitle}>
        <p>About the opportunity</p>
      </div>
      <div className={styles.contentText}>
        This isn't your typical job offer. We are <Link to={'https://foundermatcha.com'} target="_blank" rel="noopener noreferrer" className={styles.link}>Foundermatcha</Link>, a smart matchmaking platform for early-stage startup founders and software engineers, and one of the founders in our network is offering this role.
        <br></br>
        <br></br>
        You can apply for it via our platform (we use a psychology-backed approach to understand if you are a perfect match) and you can also join our community to find even more startup founders seeking tech co-founders (or choose not to join, it’s ok).
        <br></br>
        <br></br>
        We have high standards for data privacy, and you can learn more about our <Link to={'/privacy-policy.html'} target="_blank" rel="noopener noreferrer" className={styles.link}>Privacy Policy</Link>.
      </div>

      {description.map((chapter, index) => (
        <div key={index}>
          <div className={styles.contentTitle}><ReactMarkdown>{chapter.title}</ReactMarkdown></div>
          <div className={styles.contentText}>
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              components={{ a: ({ node, children, ...props }) => <a className={styles.link} target="_blank" rel="noopener noreferrer" {...props}>{children}</a> }}
            >
              {chapter.content}
            </ReactMarkdown>
          </div>
        </div>
      ))}

      <div className={styles.contentTitle}>
        <p>How to apply</p>
      </div>
      <div className={styles.contentText}>
        Step 1: Click "Apply now" to access the Foundermatcha platform.
        <br></br>
        <br></br>
        Step 2: Create a new account.
        <br></br>
        <br></br>
        Step 3: Describe your vision, skills, and complete a personality self-test.
        <br></br>
        <br></br>
        Step 4: Submit your application.
        <br></br>
        <br></br>
        Step 5: You can apply to this job opening only or tick the "I'm open" option to get access to other Founding Engineer opportunities in the Foundermatcha app.
      </div>

      {applyButton && (
        <div className={styles.buttonWrapper}>
          <ApplyButtonComponent isAuthenticated={isAuthenticated} jobRoleId={jobRoleId} />
        </div>
      )}
    </div>
  );
};

export default PositionDescriptionUi;
